<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import DataLayer from '@services/data-layer/index'
import MixGridBlock from '@components/mix-grid-block'
import { shuffleArray } from '@utils/arrays'
import { useAuthStore } from '@state/stores/auth'
import { BREAKPOINT_DESKTOP, isDesktopWidth } from '@design/breakpoints'

const HOT_DROPS_FETCH_LIMIT = 10
const DESKTOP_DISPLAY_LIMIT = 4
const MOBILE_DISPLAY_LIMIT = 4

const authStore = useAuthStore()

const mixes = ref([])
const windowWidth = ref(BREAKPOINT_DESKTOP)

const currentUserId = computed(() => authStore.currentUser?.members_id?.id)

const isDesktop = computed(() => isDesktopWidth(windowWidth.value))

const skeletonCount = computed(() => (isDesktop.value ? DESKTOP_DISPLAY_LIMIT : MOBILE_DISPLAY_LIMIT))

const displayedMixes = computed(() =>
    mixes.value.slice(0, isDesktop.value ? DESKTOP_DISPLAY_LIMIT : MOBILE_DISPLAY_LIMIT)
)

function onResize() {
    windowWidth.value = window.innerWidth
}

async function getRecommendedMixes() {
    if (!currentUserId.value) return

    const response = await DataLayer.mixes.getRecommended(currentUserId.value, HOT_DROPS_FETCH_LIMIT)
    mixes.value = shuffleArray(response.body)
}

onMounted(() => {
    onResize()
    getRecommendedMixes()
    window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
})
</script>

<template>
    <div>
        <BaseSection
            v-if="mixes.length"
            title="Hot Drops for you"
            subtitle="Mixes we think you'll dig"
        >
            <MixGridBlock :mixes="displayedMixes"/>
        </BaseSection>
        <BaseSection
            v-else
            title="Hot Drops for you"
            subtitle="Mixes we think you'll dig"
        >
            <div class="skeleton">
                <div
                    v-for="i in skeletonCount"
                    :key="i"
                    class="skeleton-tile"
                >
                    <div class="skeleton-cover" />
                    <BasePlaceholder
                        type="artist"
                        tag="div"
                        class="skeleton-title"
                    />
                    <BasePlaceholder
                        type="show"
                        tag="div"
                        class="skeleton-subtitle"
                    />
                </div>
            </div>
        </BaseSection>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.skeleton {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: $s-m;

    @include tablet {
        grid-template-columns: repeat(3, 1fr);
    }

    @include desktop {
        grid-template-columns: repeat(4, 1fr);
    }
}

.skeleton-cover {
    aspect-ratio: 1;
    background: currentColor;
    border-radius: $radius-3;
    opacity: 0.1;
}

.skeleton-title {
    margin-top: $size-grid-padding;
}

.skeleton-subtitle {
    margin-top: $s-xs;
}
</style>
